import { useMemo } from "react";
import type { RowPayload, PerPublisher } from "../lib/schema";
import {
  groupByPublisher,
  orderedPublishers,
  PINNED_PUBLISHERS,
  type RowForAggregation,
} from "../lib/publishers";
import { heatColor, heatTextColor } from "../lib/palette";
import { pct } from "../lib/format";

interface Props {
  rows: RowPayload[];
  minRows?: number;
}

type PublisherRow = PerPublisher[string];

interface FieldGroup {
  label: string;
  metrics: { key: keyof PublisherRow; short: "P" | "R" | "•"; fallback: keyof PublisherRow }[];
}

const GROUPS: FieldGroup[] = [
  {
    label: "Authors",
    metrics: [
      { key: "authors_precision_soft", short: "P", fallback: "authors_f1_soft" },
      { key: "authors_recall_soft", short: "R", fallback: "authors_f1_soft" },
    ],
  },
  {
    label: "Affiliations",
    metrics: [
      { key: "affiliations_precision_fuzzy", short: "P", fallback: "affiliations_f1_fuzzy" },
      { key: "affiliations_recall_fuzzy", short: "R", fallback: "affiliations_f1_fuzzy" },
    ],
  },
  {
    label: "Abstract",
    metrics: [
      { key: "abstract_match_rate", short: "•", fallback: "abstract_ratio_fuzzy" },
    ],
  },
  {
    label: "PDF URL",
    metrics: [
      { key: "pdf_url_precision", short: "P", fallback: "pdf_url_accuracy" },
      { key: "pdf_url_recall", short: "R", fallback: "pdf_url_accuracy" },
    ],
  },
];

const PINNED_SET = new Set<string>(PINNED_PUBLISHERS);

function toAggregationRows(rows: RowPayload[]): RowForAggregation[] {
  return rows.map((r) => ({
    doi: r.doi,
    link: r.link,
    publisher_domain: r.publisher_domain,
    score: {
      authors: r.score.authors
        ? {
            precision_soft: r.score.authors.precision_soft,
            recall_soft: r.score.authors.recall_soft,
            f1_soft: r.score.authors.f1_soft,
          }
        : null,
      affiliations: r.score.affiliations
        ? {
            fuzzy_f1: r.score.affiliations.fuzzy_f1,
            fuzzy_precision: r.score.affiliations.fuzzy_precision,
            fuzzy_recall: r.score.affiliations.fuzzy_recall,
          }
        : null,
      abstract: {
        fuzzy_ratio: r.score.abstract.fuzzy_ratio,
        match_at_threshold: r.score.abstract.match_at_threshold,
      },
      pdf_url: {
        strict_match: r.score.pdf_url.strict_match,
        present: r.score.pdf_url.present,
        expected_present: r.score.pdf_url.expected_present,
        divergent: r.score.pdf_url.divergent,
      },
      error: r.score.error,
    },
  }));
}

export function PublisherBreakdown({ rows, minRows = 1 }: Props) {
  const ordered = useMemo(() => {
    const data = groupByPublisher(toAggregationRows(rows));
    return orderedPublishers(data).filter(([, stats]) => stats.rows >= minRows);
  }, [rows, minRows]);

  if (ordered.length === 0) {
    return <p className="empty-state">No publishers with ≥ {minRows} gold-standard rows.</p>;
  }

  return (
    <div className="heatmap card">
      <table>
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: "26%" }}>Publisher</th>
            {GROUPS.map((g) => (
              <th
                key={g.label}
                colSpan={g.metrics.length}
                style={{ textAlign: "center", borderBottom: "1px solid var(--hairline-soft)" }}
              >
                {g.label}
              </th>
            ))}
            <th rowSpan={2} style={{ textAlign: "right", paddingRight: "var(--space-3)" }}>N</th>
          </tr>
          <tr>
            {GROUPS.flatMap((g) =>
              g.metrics.map((m) => (
                <th
                  key={`${g.label}-${m.short}-${String(m.key)}`}
                  style={{
                    textAlign: "center",
                    fontSize: "var(--text-micro)",
                    color: "var(--ink-faint)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                  }}
                >
                  {m.short === "•" ? "match" : m.short}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {ordered.map(([publisher, stats]) => {
            const isPinned = PINNED_SET.has(publisher);
            return (
              <tr key={publisher}>
                <td
                  className="label"
                  style={{
                    fontWeight: isPinned ? 600 : 400,
                    color: isPinned ? "var(--ink)" : "var(--ink-muted)",
                  }}
                  title={
                    isPinned
                      ? `${publisher} — pinned (Casey 4/21 PM: scale-weighted impact)`
                      : publisher
                  }
                >
                  {publisher}
                </td>
                {GROUPS.flatMap((g) =>
                  g.metrics.map((m) => {
                    const raw = stats[m.key] as number | undefined;
                    const v = typeof raw === "number" ? raw : ((stats[m.fallback] as number) ?? 0);
                    const title =
                      `${publisher} · ${g.label} · ${m.short === "•" ? "match" : m.short} · ${pct(v)}` +
                      (raw === undefined ? " (legacy fallback)" : "");
                    return (
                      <td
                        key={`${publisher}-${g.label}-${m.short}-${String(m.key)}`}
                        className="cell"
                        style={{ background: heatColor(v), color: heatTextColor(v) }}
                        title={title}
                      >
                        {pct(v)}
                      </td>
                    );
                  })
                )}
                <td
                  style={{
                    textAlign: "right",
                    fontFamily: "var(--font-mono)",
                    color: "var(--ink-muted)",
                    paddingRight: "var(--space-3)",
                    fontSize: "var(--text-micro)",
                  }}
                >
                  {stats.rows}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
